/**
 * React Router Configuration for TECDocChart Root-Level Shell
 * 
 * Implements Issue #68 - Root-Level React Router & Django URL Restructure
 * Simplified approach for Issue #69 - Simple page-to-page navigation between React and AngularJS
 * Issue #94 - Smart routing based on single_user configuration for AI client
 * 
 * Provides client-side routing for healthcare application with protected routes.
 * Legacy AngularJS modules are accessed via standard navigation links.
 */

import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import ShellLayout from '../components/layout/ShellLayout';
import HealthCheckDashboard from '../components/HealthCheck/HealthCheckDashboard';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import DashboardPage from '../components/pages/DashboardPage';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import { AngularIntegrationTest } from '../components/legacy';
import DefaultRoute from '../components/routing/DefaultRoute';
import DirectoryPage from '../pages/directory/DirectoryPage';
import DirectoryErrorBoundary from '../pages/directory/components/DirectoryErrorBoundary';
import EmployeeDetailPage from '../pages/directory/EmployeeDetailPage';
import ReferralPage from '../pages/directory/ReferralPage';
import { LoginPage } from '../pages/auth';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import PasswordResetConfirmPage from '../pages/auth/PasswordResetConfirmPage';
import SelfRegistrationPage from '../pages/auth/SelfRegistrationPage';
import LabUploadPage from '../pages/auth/LabUploadPage';
// AngularJSEncounterRedirect import removed — encounters now use React routes
// Lazy load Encounters module for code splitting
const EncountersPage = lazy(() => import('../pages/encounters/EncountersPage'));
const EncounterDetailLayout = lazy(() =>
  import('../pages/encounters/contexts/EncounterDetailContext').then(m => ({ default: m.EncounterDetailLayout }))
);
const EncounterOverviewLayout = lazy(() =>
  import('../pages/encounters/contexts/EncounterOverviewContext').then(m => ({ default: m.EncounterOverviewLayout }))
);
const EncounterOverview = lazy(() => import('../pages/encounters/components/EncounterOverview'));
const EncounterChart = lazy(() => import('../pages/encounters/components/EncounterChart'));
const EncounterWorkup = lazy(() => import('../pages/encounters/components/EncounterWorkup'));
const EncounterChartReviewRedirect = lazy(() => import('../pages/encounters/components/EncounterChartReviewRedirect'));
const EncounterOrders = lazy(() => import('../pages/encounters/components/EncounterOrders'));
const EncounterVitals = lazy(() => import('../pages/encounters/components/EncounterVitals'));
const EncounterEKGs = lazy(() => import('../pages/encounters/components/EncounterEKGs'));
const OrderArtifacts = lazy(() => import('../pages/encounters/components/OrderArtifacts'));
const EncounterDischarge = lazy(() => import('../pages/encounters/components/EncounterDischarge'));
const EncounterMedia = lazy(() => import('../pages/encounters/components/EncounterMedia'));
const EncounterLabs = lazy(() => import('../pages/encounters/components/EncounterLabs'));
const EncounterHistory = lazy(() => import('../pages/encounters/components/EncounterHistory'));
const EncounterSBAR = lazy(() => import('../pages/encounters/components/EncounterSBAR'));
const EncounterReview = lazy(() => import('../pages/encounters/components/EncounterReview'));
const EncounterClose = lazy(() => import('../pages/encounters/components/EncounterClose'));
const QualityChartReviewPage = lazy(() => import('../pages/encounters/components/QualityChartReviewPage'));

// Lazy load Reports module for code splitting
const ReportPage = lazy(() => import('../pages/reports/ReportPage'));

// Lazy load Patients module for code splitting
const PatientsPage = lazy(() => import('../pages/patients/PatientsPage'));
const EncounterDetail = lazy(() => import('../pages/patients/components/EncounterDetail'));
const PatientSearchEntry = lazy(() => import('../pages/patients/components/PatientSearchEntry'));
const PatientHistory = lazy(() => import('../pages/patients/components/PatientHistory'));
const PatientMerge = lazy(() => import('../pages/patients/components/PatientMerge'));

// Lazy load HR module for code splitting
const EmployeeListPage = lazy(() => import('../pages/hr/EmployeeListPage'));
const EmployeeEditorPage = lazy(() => import('../pages/hr/EmployeeEditorPage'));
const JobsPage = lazy(() => import('../pages/hr/JobsPage'));
const JobDetailPage = lazy(() => import('../pages/hr/JobDetailPage'));
const RolesPage = lazy(() => import('../pages/hr/RolesPage'));
const RoleDetailPage = lazy(() => import('../pages/hr/RoleDetailPage'));
const PermissionsPage = lazy(() => import('../pages/hr/PermissionsPage'));
const PermissionEditorPage = lazy(() => import('../pages/hr/PermissionEditorPage'));
const BroadcastPage = lazy(() => import('../pages/hr/BroadcastPage'));
// Lazy load TECdoc Scribe (AI client) module for code splitting
const AiEncounterListPage = lazy(() => import('../pages/ai/AiEncounterListPage'));
const AiEncounterDetailPage = lazy(() => import('../pages/ai/AiEncounterDetailPage'));

const AdminSettingsPage = lazy(() => import('../pages/admin/AdminSettingsPage'));
const AccountPage = lazy(() => import('../pages/account/AccountPage'));

/**
 * Root-level React Router configuration
 * 
 * This replaces Django URL routing for the main application.
 * All routes are authenticated by default using ProtectedRoute wrapper.
 * AngularJS modules will be progressively integrated as needed.
 */
export const router = createBrowserRouter([
  {
    // Login page - standalone route WITHOUT shell layout for unauthenticated users
    path: "/login",
    element: <LoginPage />
  },
  {
    // Forgot password page - standalone route for unauthenticated users
    path: "/forgot-password",
    element: <ForgotPasswordPage />
  },
  {
    // Password reset confirm page - standalone route for unauthenticated users
    path: "/reset-password/:uidb64/:token",
    element: <PasswordResetConfirmPage />
  },
  {
    // Patient self-registration portal (Issue #274) - standalone external
    // unauthenticated route. Optional facility code as the last path segment.
    path: "/auth/self_registration/:facilityCode?",
    element: <SelfRegistrationPage />
  },
  {
    // External lab-result upload portal — standalone, unauthenticated (facility-code gated).
    // React migration of the AngularJS lab_upload page (issue #273).
    path: "/auth/lab_upload/:facilityCode?",
    element: <LabUploadPage />
  },
  {
    // Email deep-link for a single read-only chart. This intentionally bypasses
    // ShellLayout so quality-only doctors cannot navigate to other modules.
    path: "/quality/chart/:encounterId",
    element: (
      <ProtectedRoute requiredPermissions={['quality_chart_view', 'patient_encounter', 'patient_history']}>
        <QualityChartReviewPage />
      </ProtectedRoute>
    ),
    children: [
      {
        element: <EncounterDetailLayout />,
        children: [
          { index: true, element: <EncounterReview readOnly /> },
        ],
      },
    ],
  },
  {
    path: "/",
    element: <ShellLayout />,
    errorElement: (
      <ErrorBoundary>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Route Error</h1>
          <p>An error occurred while loading this page.</p>
          <button onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      </ErrorBoundary>
    ),
    children: [
      {
        // Root path - smart routing based on single_user configuration (Issue #94)
        // single_user = true: Routes to encounters (AI client)
        // single_user = false: Routes to dashboard (EMR client)
        index: true,
        element: <DefaultRoute />
      },
      {
        // Health dashboard (for system monitoring)
        path: "health",
        element: (
          <ProtectedRoute>
            <HealthCheckDashboard />
          </ProtectedRoute>
        )
      },
      {
        // Main dashboard (default healthcare landing page)
        path: "dashboard",
        element: (
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        )
      },
      {
        // Directory - React implementation with intelligent routing
        path: "directory",
        element: (
          <ProtectedRoute>
            <DirectoryErrorBoundary fallbackMessage="The employee directory encountered an error. The page includes mock data for development and testing purposes.">
              <DirectoryPage useReactVersion={true} />
            </DirectoryErrorBoundary>
          </ProtectedRoute>
        )
      },
      {
        // Employee Detail - React implementation of employee profile view
        path: "directory/employee/:employeeId",
        element: (
          <ProtectedRoute>
            <DirectoryErrorBoundary fallbackMessage="Failed to load employee details.">
              <EmployeeDetailPage />
            </DirectoryErrorBoundary>
          </ProtectedRoute>
        )
      },
      {
        // Referral Directory - React implementation with filtering and editing
        path: "directory/referrals",
        element: (
          <ProtectedRoute>
            <DirectoryErrorBoundary fallbackMessage="Failed to load referral directory.">
              <ReferralPage />
            </DirectoryErrorBoundary>
          </ProtectedRoute>
        )
      },
      {
        // Admin / Settings - React migration entrypoint
        path: "admin/settings",
        element: (
          <ProtectedRoute requiredPermissions={['admin']}>
            <AdminSettingsPage />
          </ProtectedRoute>
        )
      },
      {
        // Admin / Settings nested paths for future section expansion
        path: "admin/settings/*",
        element: (
          <ProtectedRoute requiredPermissions={['admin']}>
            <AdminSettingsPage />
          </ProtectedRoute>
        )
      },
      {
        // Future React modules will go here
        // For now, AngularJS modules are accessed via standard navigation (e.g., /patients/, /encounters/)
        // This maintains simple architecture while allowing gradual React migration

        // Patients - React implementation with encounter list views
        path: "patients",
        element: (
          <ProtectedRoute>
            <PatientsPage />
          </ProtectedRoute>
        )
      },
      {
        // Duplicate route to handle trailing slash from navigation
        path: "patients/",
        element: (
          <ProtectedRoute>
            <PatientsPage />
          </ProtectedRoute>
        )
      },
      {
        // Patient Search/Entry - React implementation of new encounter entry
        // Must come before :id route to avoid being matched as a dynamic segment
        path: "patients/start/entry",
        element: (
          <ProtectedRoute>
            <PatientSearchEntry />
          </ProtectedRoute>
        )
      },
      {
        // Patient Pre-Registration - React implementation
        path: "patients/register/entry",
        element: (
          <ProtectedRoute>
            <PatientSearchEntry />
          </ProtectedRoute>
        )
      },
      {
        // Patient Editor (New Encounter) - React implementation
        // Receives patient data via location.state from PatientSearchEntry
        path: "patients/start/editor",
        element: (
          <ProtectedRoute>
            <EncounterDetail />
          </ProtectedRoute>
        )
      },
      {
        // Patient Editor (Pre-Registration) - React implementation
        // Receives patient data via location.state from PatientSearchEntry
        path: "patients/register/editor",
        element: (
          <ProtectedRoute>
            <EncounterDetail />
          </ProtectedRoute>
        )
      },
      {
        // Patient History - React implementation of patient encounter history view
        path: "patients/history/:patientId",
        element: (
          <ProtectedRoute>
            <PatientHistory />
          </ProtectedRoute>
        )
      },
      {
        // Patient Merge - React implementation of patient merge view
        path: "patients/merge/:survivorId",
        element: (
          <ProtectedRoute>
            <PatientMerge />
          </ProtectedRoute>
        )
      },
      {
        // Patient/Encounter Detail - React implementation of encounter detail view
        path: "patients/:id",
        element: (
          <ProtectedRoute>
            <EncounterDetail />
          </ProtectedRoute>
        )
      },
      {
        // Encounters list page
        path: "encounters",
        element: (
          <ProtectedRoute requiredPermissions={['patient_encounter', 'patient_history']}>
            <EncountersPage />
          </ProtectedRoute>
        )
      },
      {
        // Active encounter detail — layout wrapper that loads encounter data
        path: "encounters/active/:encounterId",
        element: (
          <ProtectedRoute requiredPermissions={['patient_encounter', 'hr_admin']}>
            <EncounterDetailLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: "overview",
            element: <EncounterOverviewLayout />,
            children: [
              {
                path: "",
                element: <EncounterOverview />,
                children: [
                  {
                    path: "chart",
                    element: <EncounterChart />,
                    children: [
                      // Static segment before the dynamic one: /workup/review is the
                      // chart-review deep link and must not be read as workupIndex="review".
                      { path: "workup/review", element: <EncounterChartReviewRedirect /> },
                      { path: "workup/:workupIndex", element: <EncounterWorkup /> },
                      { path: "orders", element: <EncounterOrders /> },
                      { path: "vitals", element: <EncounterVitals /> },
                      { path: "ekgs", element: <EncounterEKGs /> },
                      { path: "artifacts/:orderId", element: <OrderArtifacts /> },
                      { path: "disposition", element: <EncounterDischarge /> },
                    ]
                  },
                  { path: "media", element: <EncounterMedia /> },
                  { path: "labs", element: <EncounterLabs /> },
                  { path: "history", element: <EncounterHistory /> },
                  { path: "sbar", element: <EncounterSBAR /> },
                  { path: "review", element: <EncounterReview /> },
                ]
              },
            ]
          },
        ]
      },
      {
        // Open encounter — close workflow
        path: "encounters/open/:encounterId",
        element: (
          <ProtectedRoute requiredPermissions={['patient_encounter', 'patient_history']}>
            <EncounterDetailLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: "close",
            element: (
              <ProtectedRoute requiredPermissions={['patient_encounter', 'hr_admin']}>
                <EncounterClose />
              </ProtectedRoute>
            ),
          },
          {
            path: "review",
            element: <EncounterOverviewLayout />,
            children: [
              {
                path: "",
                element: <EncounterOverview />,
                children: [
                  { path: "", element: <EncounterReview /> },
                ]
              },
            ]
          },
        ]
      },
      // ========== TECdoc Scribe (AI client) Routes ==========
      {
        // AI patient list — active encounters
        path: "ai/list",
        element: (
          <ProtectedRoute>
            <AiEncounterListPage />
          </ProtectedRoute>
        )
      },
      {
        // AI patient list — archived encounters
        path: "ai/archived",
        element: (
          <ProtectedRoute>
            <AiEncounterListPage archived />
          </ProtectedRoute>
        )
      },
      {
        // AI encounter detail — artifact capture + generated chart
        path: "ai/detail/:encounterId",
        element: (
          <ProtectedRoute>
            <AiEncounterDetailPage />
          </ProtectedRoute>
        )
      },
      // ========== END: TECdoc Scribe Routes ==========
      {
        // Reports - React implementation with filtering and export
        // Handle both /reports and /reports/ for AngularJS compatibility
        path: "reports",
        element: (
          <ProtectedRoute>
            <ReportPage />
          </ProtectedRoute>
        )
      },
      {
        // Duplicate route to handle trailing slash from AngularJS navigation
        path: "reports/",
        element: (
          <ProtectedRoute>
            <ReportPage />
          </ProtectedRoute>
        )
      },
      {
        path: "reports-react",
        element: (
          <ProtectedRoute>
            <div style={{ padding: '20px' }}>
              <h2>Reports Module (React)</h2>
              <p>Future React implementation of reports module will go here.</p>
              <p>Current reports module: <a href="/reports/">/reports/</a> (AngularJS via Django)</p>
            </div>
          </ProtectedRoute>
        )
      },
      {
        // Integration test/reference page (kept for documentation)
        path: "integration-test",
        element: (
          <ProtectedRoute>
            <AngularIntegrationTest />
          </ProtectedRoute>
        )
      },
      {
        // Signed-in user's self-service account profile
        path: "account",
        element: (
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        )
      },
      // ========== HR Module Routes ==========
      {
        // HR Employee List - React implementation
        path: "hr/employees",
        element: (
          <ProtectedRoute>
            <EmployeeListPage showCandidates={false} />
          </ProtectedRoute>
        )
      },
      {
        // HR Candidates List - React implementation
        path: "hr/candidates",
        element: (
          <ProtectedRoute>
            <EmployeeListPage showCandidates={true} />
          </ProtectedRoute>
        )
      },
      {
        // HR Candidate Editor (new) - React implementation
        path: "hr/candidates/new",
        element: (
          <ProtectedRoute>
            <EmployeeEditorPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Candidate Editor (edit) - React implementation
        path: "hr/candidates/:id",
        element: (
          <ProtectedRoute>
            <EmployeeEditorPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Employee Editor (new) - React implementation
        path: "hr/employees/new",
        element: (
          <ProtectedRoute>
            <EmployeeEditorPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Employee Editor (edit) - React implementation
        path: "hr/employees/:id",
        element: (
          <ProtectedRoute>
            <EmployeeEditorPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Jobs Management - React implementation
        path: "hr/jobs",
        element: (
          <ProtectedRoute>
            <JobsPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Job Detail/Editor - React implementation
        path: "hr/jobs/:jobId",
        element: (
          <ProtectedRoute>
            <JobDetailPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Roles Management - React implementation
        path: "hr/roles",
        element: (
          <ProtectedRoute>
            <RolesPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Role Detail/Editor - React implementation
        path: "hr/roles/:roleId",
        element: (
          <ProtectedRoute>
            <RoleDetailPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Permissions Management - React implementation
        path: "hr/permissions",
        element: (
          <ProtectedRoute>
            <PermissionsPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Permission Editor (new) - React implementation
        path: "hr/permissions/new",
        element: (
          <ProtectedRoute>
            <PermissionEditorPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Permission Editor (edit) - React implementation
        path: "hr/permissions/:id",
        element: (
          <ProtectedRoute>
            <PermissionEditorPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Broadcast Messages - React implementation
        path: "hr/broadcast",
        element: (
          <ProtectedRoute>
            <BroadcastPage />
          </ProtectedRoute>
        )
      },
      {
        // HR Module Root - Redirect to employees
        path: "hr",
        element: (
          <ProtectedRoute>
            <EmployeeListPage showCandidates={false} />
          </ProtectedRoute>
        )
      },
      // ========== END: Encounter Routes ==========
      {
        // Catch-all for unhandled routes
        path: "*",
        element: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Page Not Found</h2>
            <p>The requested page could not be found in the React shell.</p>
            <p>This may be a Django-handled route or legacy AngularJS module.</p>
            <button onClick={() => window.history.back()}>
              Go Back
            </button>
            {' | '}
            <button onClick={() => window.location.href = '/dashboard'}>
              Go to Dashboard
            </button>
          </div>
        )
      }
    ]
  }
]);

export default router;
